import { Inject } from '@odinjs/odin'

import { ActionAPI, ButtonAPI, DeckAPI, ModalService, PanelAPI } from '@philips-internal/oswald-ui-components';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { Value } from '@philips-internal/oswald-ui-core';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext , foundationContext} from '@philips-internal/oswald-ui-foundation';

import { RecommendationProps, RecommendationStatusItem } from './recommendation-params';
import { Params } from '../administration-schedule/administration-schedule-types';
import { MedicineAndSolutionsParams } from '../medicine-and-solutions/medicine-and-solutions-params';
import { MedicationViewerParams } from '../medication-viewer/medication-viewer-params';
import { Function9129Param13Values } from '../oral-supplements-viewer/oral-supplements-params';
import TimelineContext, { TimeLineEventMedication } from '../../services/timeline-service';
import BaseController from '../../../BaseController';
import { MedicationAndDateHeaderParams } from '../../../commons/header/medication-and-date-header-params';
import { ScheduleMedicationParams } from '../../../schedule-medication-infusion/schedule-medication-infusion-params';
import { InfusionHourStatus } from '../../../../constants/infusion-solutions';
import { FragmentNames, RecommendationViewerAliases } from '../../../../codegen';
import InformationBarUtils from '../../../../controllers/commons/header/information-bar-header-utils';
import { Endpoint } from '../../../../constants/endpoints';
import ShowToast from '../../../../utils/toast-service';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { ADEP_E1113Parameters, ADEP_E9129Parameters, FunctionName } from '../../../../utils/parameters';
import { ApplicationRoute } from '../../../../constants/routes';
import { YesNoType } from '../../../../utils/enum-utils';
import { HealthProfessionalUser } from '../../../../app/authentication';

@Controller({ component: RecommendationViewerAliases.RECOMMENDATION_VIEWER_LAYOUT, fragment: FragmentNames.RECOMMENDATION_VIEWER })
export default class RecommendationViewerController extends BaseController {

  @Inject(RecommendationViewerAliases.EXECUTE_TIME_PANEL)
  private footerPanelApi!: PanelAPI;

  @Inject(RecommendationViewerAliases.RECOMMENDATION_VIEWER_DECK)
  private recommendationViewerDeckApi!: DeckAPI;

  @OnMount(RecommendationViewerAliases.RECOMMENDATION_VIEWER_LAYOUT)
  handleMountRecommendationLayout(): void {
    InformationBarUtils.collapsedBar = true;
    this.fetchRecommendation().then((response) => {
      const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
      const recommendationInformation = response as RecommendationProps;
      const infoStatus = TimelineContext.get().status;
      const itemQuantity = String(1);
      const observation = recommendationInformation.observationDescription || '';
      const recommendationData = this.recommendationViewerDeckApi.getData() ?? [];

      if (observation && recommendationData[0]) {
        recommendationData[0].observation = observation;
      }

      MedicineAndSolutionsParams.params = {
        ...MedicineAndSolutionsParams.params,
        cdIntervalId: recommendationInformation.intervalCode,
        nrSequenceId: recommendationInformation.itemSequence,
        qtDoseId: itemQuantity,
      };

      const infoData: Record<string, Value> = {
        statusItem: infoStatus,
      };

      this.context.set({ ...recommendationInformation, encounterId, infoData, observation, patientId });

      this.recommendationViewerDeckApi.setData(recommendationData);
      this.addNewParams();

      if (infoData.statusItem === InfusionHourStatus.PENDING) {
        this.footerPanelApi.setVisible(true);
      }

    }).catch((error) => {
      if (error.body) {
        ShowToast.open(error.body.errorMessage, ToastLevel.ERROR);
      }
    })
  }

  @OnMount(RecommendationViewerAliases.RECOMMENDATION_VIEWER_DECK)
  handleMountDeck({ target }: EventData<DeckAPI>): void {
    const timelineRecommendation = TimelineContext.get() as TimeLineEventMedication;

    target.setData([{
      description: timelineRecommendation.description,
      prescriptionNumber: timelineRecommendation.prescriptionNumber,
      scheduleDate: timelineRecommendation.start,
      statusItem: timelineRecommendation.status
    }]);

  }

  @OnMount(RecommendationViewerAliases.EXECUTE_TIME_BUTTON)
  handleMountExecuteTimeButton(event: MountEvent): void {
    const buttonApi = event.target as ButtonAPI;
    const allowed = this.validateExecuteTimeButton();
    buttonApi.setVisible(allowed);
    this.footerPanelApi.setVisible(allowed);
  }

  validateExecuteTimeButton(): boolean {
    const param13 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.ALLOWS_TO_PERFORM_PRESCRIPTION_ITEMS) as string;
    return [Function9129Param13Values.YES, Function9129Param13Values.DEPENDS_ON_THE_RULE].includes(param13);
  }

  @OnClick(RecommendationViewerAliases.EXECUTE_TIME_BUTTON)
  handleClickExecuteButton(): void {
    ModalService.openDefault({
      contextData: this.context.getBulkData() as Record<string, Value>,
      fragment: FragmentNames.RECOMMENDATION_EXECUTE_TIME_MODAL,
      id: FragmentNames.RECOMMENDATION_EXECUTE_TIME_MODAL,
      title: 'text.emar-record',
    })
  }

  private fetchRecommendation(): Promise<RecommendationProps> {
    const { scheduleSequenceNumber } = this.historyService.getCurrentRouteParams();
    const { cpoeSequence } = TimelineContext.get()

    return HttpService.get<RecommendationProps>(Endpoint.EMAR_GET_RECOMMENDATION_INFORMATION, {
      recommendationId: cpoeSequence,
      scheduleSequenceNumber,
    }).then((response) => {
      const responseBody = response.body as RecommendationProps;
      this.context.set({ ...responseBody });
      this.footerPanelApi.setVisible(!this.wasRegistered() && this.validateExecuteTimeButton());
      return responseBody;
    })
  }

  @OnMount(RecommendationViewerAliases.RESCHEDULE_RECOMMENDATION_ACTION)
  handleMountRescheduleAction(event: MountEvent): void {
    const actionApi = event.target as ActionAPI;
    const allowReschedule = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_RESCHEDULE_MEDICATION);
    actionApi.setEnabled(allowReschedule === Params.ALLOWED && !this.wasRegistered());
  }

  @OnClick(RecommendationViewerAliases.RESCHEDULE_RECOMMENDATION_ACTION)
  handleClickRescheduleRecommentationAction(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const route = ApplicationRoute.EMAR_SCHEDULE_MEDICATION_INFUSION;
    applicationContext.redirect(route, { encounterId, patientId });
  }

  private wasRegistered(): boolean {
    const statusItem = TimelineContext.get().status;
    return [RecommendationStatusItem.ADMINISTERED, RecommendationStatusItem.RECOMMENDATION_ADMINISTERED].includes(statusItem as string);
  }

  private addNewParams(): void {
    const timelineRecommendation = TimelineContext.get() as TimeLineEventMedication;
    const { scheduleSequenceNumber } = this.historyService.getCurrentRouteParams();

    MedicationViewerParams.adminSchedule = {
      dilutionDescription: '',
      prescriptionNumber: Number(timelineRecommendation.prescriptionNumber)
    };
    MedicationAndDateHeaderParams.medicationAndDateHeaderProps = {
      medicationName: timelineRecommendation.description,
      scheduleDate: timelineRecommendation.start,
      type: 'RECOMMENDATIONS'
    };
    ScheduleMedicationParams.prototype.scheduleSequenceNumber = Number(scheduleSequenceNumber);
  }

  @OnMount(RecommendationViewerAliases.SUSPEND_RECOMMENDATION_ACTION)
  handleMountSuspendRecommentationAction(event: MountEvent): void {
    const actionApi = event.target as ActionAPI;
    actionApi.setEnabled(this.shouldShowSuspendMedicationAction() && !this.wasRegistered())
  }

  @OnClick(RecommendationViewerAliases.SUSPEND_RECOMMENDATION_ACTION)
  handleClickSuspendRecommentatioAction(): void {
    const { patientId, encounterId } = this.context.getBulkData() as Record<string, string>;
    applicationContext.redirect(ApplicationRoute.ADEP_SUSPEND_ITEM, {
      encounterId,
      patientId,
    });
  }

  private shouldShowSuspendMedicationAction(): boolean {
    const user = foundationContext.getUser() as HealthProfessionalUser;
    if (user.isDoctor) {
      const allowMedicalUsersSuspendSchedule = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_MEDICAL_USERS_SUSPEND_SCHEDULES);
      return allowMedicalUsersSuspendSchedule !== YesNoType.NO;
    }
    const allowOnlyRevert = 'R';
    const allowNonMedicalSuspendOrReverseSuspension = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_NON_MEDICAL_SUSPEND_OR_REVERSE_SUSPENSIONS);

    return allowNonMedicalSuspendOrReverseSuspension !== YesNoType.NO.valueOf() && allowNonMedicalSuspendOrReverseSuspension !== allowOnlyRevert;
  }
}
